import { useSessionStorage } from '@reactuses/core';
import { clearIdentity, log, setIdentity } from 'evlog/client';
import { FirebaseError } from 'firebase/app';
import {
  AuthErrorCodes,
  PhoneAuthProvider,
  getAuth,
  initializeRecaptchaConfig,
  onAuthStateChanged,
  onIdTokenChanged,
  signInWithCredential,
  signInWithPhoneNumber,
} from 'firebase/auth';
import { useEffect } from 'react';

import {
  client,
  firebaseApp,
  logout as firebaseLogout,
  getSubject,
  useData,
  useSubject,
} from '@virtual-care/ui';

export const auth = getAuth(firebaseApp);
auth.useDeviceLanguage();

initializeRecaptchaConfig(auth).catch((error) => {
  log.error({ action: 'recaptcha_init', error });
});

// Error keys constants — resolved to translated copy by the presentation layer.
const ERROR_KEYS = {
  INVALID_CREDENTIALS: 'shared.firebase.invalidCredentials',
  TOO_MANY_ATTEMPTS: 'shared.firebase.tooManyAttempts',
  ACCOUNT_DISABLED: 'shared.firebase.accountDisabled',
  NETWORK_FAILED: 'shared.firebase.networkFailed',
  LOGIN_NOT_SUPPORTED: 'shared.firebase.loginNotSupported',
  INVALID_EMAIL: 'shared.firebase.invalidEmail',
  INVALID_PASSWORD: 'shared.firebase.invalidPassword',
  LOGIN_ERROR: 'shared.firebase.loginError',
  LOGOUT_ERROR: 'shared.firebase.logoutError',

  INVALID_PHONE: 'shared.firebase.invalidPhone',
  EMAIL_IN_USE: 'shared.firebase.emailInUse',
  PHONE_IN_USE: 'shared.firebase.phoneInUse',
  ACCOUNT_EXISTS_DIFFERENT_METHOD:
    'shared.firebase.accountExistsDifferentMethod',
  RECAPTCHA_ERROR: 'shared.firebase.recaptchaError',
  SIGNUP_ERROR: 'shared.firebase.signupError',

  CONFIRMATION_NOT_FOUND: 'shared.firebase.confirmationNotFound',
  INVALID_CODE: 'shared.firebase.invalidCode',
  PHONE_VERIFICATION_ERROR: 'shared.firebase.phoneVerificationError',
  NO_ACTIVE_VERIFICATION: 'shared.firebase.noActiveVerification',

  EMAIL_NOT_REGISTERED: 'shared.firebase.emailNotRegistered',
  RESET_EMAIL_ERROR: 'shared.firebase.resetEmailError',
  RESET_CODE_ERROR: 'shared.firebase.resetCodeError',
};

export async function signup(phoneNumber: string) {
  try {
    await sendOtp(phoneNumber);
  } catch (error) {
    if (error instanceof FirebaseError) {
      switch (error.code) {
        case AuthErrorCodes.MISSING_PHONE_NUMBER:
        case AuthErrorCodes.INVALID_PHONE_NUMBER:
          throw new Error(ERROR_KEYS.INVALID_PHONE);
        case AuthErrorCodes.EMAIL_EXISTS:
          throw new Error(ERROR_KEYS.EMAIL_IN_USE);
        case 'auth/phone-number-already-exists':
          // This error is thrown when the phone number is already linked to another account
          throw new Error(ERROR_KEYS.PHONE_IN_USE);
        case AuthErrorCodes.NEED_CONFIRMATION:
          throw new Error(ERROR_KEYS.ACCOUNT_EXISTS_DIFFERENT_METHOD);
        case AuthErrorCodes.PROVIDER_ALREADY_LINKED:
          throw new Error(ERROR_KEYS.PHONE_IN_USE);
        case AuthErrorCodes.RECAPTCHA_NOT_ENABLED:
        case AuthErrorCodes.MISSING_RECAPTCHA_VERSION:
        case AuthErrorCodes.MISSING_RECAPTCHA_TOKEN:
        case AuthErrorCodes.INVALID_RECAPTCHA_VERSION:
        case AuthErrorCodes.INVALID_RECAPTCHA_TOKEN:
        case AuthErrorCodes.INVALID_RECAPTCHA_ACTION:
          throw new Error(ERROR_KEYS.RECAPTCHA_ERROR);
        default:
          log.error({ action: 'signup', error });
          throw new Error(ERROR_KEYS.SIGNUP_ERROR);
      }
    }
    throw error;
  }
}

export async function sendOtp(phoneNumber: string) {
  try {
    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber);
    sessionStorage.setItem('verificationId', confirmationResult.verificationId);
    sessionStorage.setItem(
      JSON.stringify(confirmationResult.verificationId),
      phoneNumber,
    );
    sessionStorage.setItem('otpSentAt', Date.now().toString());
  } catch (error) {
    if (error instanceof FirebaseError) {
      switch (error.code) {
        case AuthErrorCodes.TOO_MANY_ATTEMPTS_TRY_LATER:
        case 'auth/quota-exceeded':
          throw new Error(ERROR_KEYS.TOO_MANY_ATTEMPTS);
        case AuthErrorCodes.MISSING_PHONE_NUMBER:
        case AuthErrorCodes.INVALID_PHONE_NUMBER:
          throw new Error(ERROR_KEYS.INVALID_PHONE);
        case AuthErrorCodes.RECAPTCHA_NOT_ENABLED:
        case AuthErrorCodes.MISSING_RECAPTCHA_VERSION:
        case AuthErrorCodes.MISSING_RECAPTCHA_TOKEN:
        case AuthErrorCodes.INVALID_RECAPTCHA_VERSION:
        case AuthErrorCodes.INVALID_RECAPTCHA_TOKEN:
        case AuthErrorCodes.INVALID_RECAPTCHA_ACTION:
          throw new Error(ERROR_KEYS.RECAPTCHA_ERROR);
      }
    }

    throw error;
  }
}

export async function confirmCode(code: string) {
  const verificationId = sessionStorage.getItem('verificationId');
  if (!verificationId) {
    // this should never be the case as the user is not allowed to enter /phone-verify
    // unless this value is set
    throw new Error(ERROR_KEYS.CONFIRMATION_NOT_FOUND);
  }

  try {
    const phoneAuthCredential = PhoneAuthProvider.credential(
      verificationId,
      code,
    );
    if (auth.currentUser) {
      const user = auth.currentUser;
      const token = await user.getIdToken(true);
      return { user, token };
    } else {
      const result = await signInWithCredential(auth, phoneAuthCredential);
      const user = result.user;
      const token = await user.getIdToken(true);
      // remove verification data from sessionStorage only after successful sign-in
      // in case error happen let the user request new code without having to repeat phone number entry
      sessionStorage.removeItem('verificationId');
      sessionStorage.removeItem('otpSentAt');
      return { user, token };
    }
  } catch (error) {
    if (error instanceof FirebaseError) {
      switch (error.code) {
        case AuthErrorCodes.CODE_EXPIRED:
        case AuthErrorCodes.INVALID_CODE:
        case AuthErrorCodes.MISSING_CODE:
          throw new Error(ERROR_KEYS.INVALID_CODE);
        default:
          log.error({ action: 'confirm_code', error });
          throw new Error(ERROR_KEYS.PHONE_VERIFICATION_ERROR);
      }
    }
    throw error;
  }
}

export function useCanConfirmCode() {
  const [verificationId, setVerificationId] = useSessionStorage<string | null>(
    'verificationId',
    null,
  );

  // Subscribe to auth changes - triggers re-render to sync state
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, () => {
      setVerificationId(sessionStorage.getItem('verificationId'));
    });
    const unsubToken = onIdTokenChanged(auth, () => {
      setVerificationId(sessionStorage.getItem('verificationId'));
    });
    return () => {
      unsubAuth();
      unsubToken();
    };
  }, [setVerificationId]);

  return {
    canConfirm: !!verificationId,
    phoneNumber: verificationId
      ? sessionStorage.getItem(JSON.stringify(verificationId))
      : undefined,
  };
}

export async function getIdentity() {
  const subject = await getSubject(auth);
  if (!subject) {
    clearIdentity();
    return null;
  }
  return client
    .request('GET /patients/me', {})
    .then((identity) => {
      setIdentity({ userId: identity.id, userType: identity.userType });
      return identity;
    })
    .catch((error) => {
      log.error({ action: 'get_identity', error });
      return null;
    });
}

/**
 * Get the database user subject
 * @returns User query
 */
export function useUser() {
  const subject = useSubject(auth);
  return useData(
    'GET /patients/me',
    {},
    { enabled: !!subject?.uid, staleTime: Infinity },
  );
}

export async function logout() {
  sessionStorage.removeItem('verificationId');
  sessionStorage.removeItem('otpSentAt');
  clearIdentity();
  await firebaseLogout(auth);
}
